import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getOrderStatus } from '../services/stripeService';
import { getDisplayUrl } from '../services/memoryService';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import './CheckoutSuccessPage.css';

function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayUrls, setDisplayUrls] = useState({});

  const sessionId = searchParams.get('session_id');

  const formatCurrency = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: (currency || 'AUD').toUpperCase(),
      }).format(Number(amount || 0));
    } catch (_) {
      return `${amount} ${currency || 'AUD'}`;
    }
  };

  const loadDisplayUrls = async (order) => {
    if (!order?.order_items) return;
    
    const urlPromises = order.order_items.map(async (item) => {
      if (item.memories) {
        const memory = item.memories;
        const imageUrl = memory.figurine_url || memory.photo_url;
        if (imageUrl) {
          try {
            const displayUrl = await getDisplayUrl(imageUrl);
            return { key: `${order.id}-${item.id}`, displayUrl };
          } catch (error) {
            console.error('Error getting display URL for order item:', item.id, error);
            return { key: `${order.id}-${item.id}`, displayUrl: imageUrl };
          }
        }
      }
      return null;
    });

    const results = await Promise.all(urlPromises);
    const urlMap = {};
    results.forEach(result => {
      if (result) {
        urlMap[result.key] = result.displayUrl;
      }
    });
    setDisplayUrls(urlMap);
  };

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found. Please complete a checkout to access this page.');
      setLoading(false);
      return;
    }

    const fetchOrderStatus = async () => {
      try {
        const { order: orderData } = await getOrderStatus(sessionId);
        setOrder(orderData);
        await loadDisplayUrls(orderData);
      } catch (err) {
        setError(err.message || 'Failed to fetch order status.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderStatus();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="checkout-success-page">
        <Navbar />
        <div className="success-container">
          <div className="loading">
            <div className="spinner"></div>
            <p>Processing your order...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="checkout-success-page">
        <Navbar />
        <div className="success-container">
          <div className="error-state">
            <h1>Error</h1>
            <p>{error}</p>
            <div className="error-actions">
              <Button
                variant="primary"
                className="back-to-home-btn"
                onClick={() => navigate('/home')}
              >
                Go to My Studio
              </Button>
              <Button
                variant="secondary"
                className="back-to-home-btn"
                onClick={() => navigate('/cart')}
              >
                View Cart
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="checkout-success-page">
        <Navbar />

        <div className="success-container">
          <div className="error-state">
            <h1>Order Not Found</h1>
            <p>
              We couldn't find your order. Please contact support if you continue to have issues.
            </p>
            <Button
              variant="secondary"
              className="back-to-home-btn"
              onClick={() => navigate('/home')}
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-success-page">
      <Navbar />

      <div className="success-container">
        <div className="success-header">
          <div className="success-icon"></div>
          <h1>Payment Successful!</h1>
          <p className="confirmation-text">
            Thank you for your purchase. Your order has been confirmed.
          </p>
          <div className="thank-you-message">
            <p>
              Thank you for supporting tiniego! We can't wait for you to get your hands on them.
            </p>
          </div>
        </div>

        <div className="order-details">
          <h2>Order Details</h2>
          <div className="order-info">
            <div className="info-row">
              <span>Order ID:</span>
              <span>{order.id}</span>
            </div>
            <div className="info-row">
              <span>Status:</span>
              <span className={`status ${order.status}`}>{order.status}</span>
            </div>
            <div className="info-row">
              <span>Total:</span>
              <span>{formatCurrency(order.total_amount, 'AUD')}</span>
            </div>
            <div className="info-row">
              <span>Date:</span>
              <span>{new Date(order.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="order-items">
          <h3>Items Ordered</h3>
          {order.order_items?.map((item) => (
            <div key={item.id} className="order-item">
              <div className="item-image">
                <img
                  src={
                    displayUrls[`${order.id}-${item.id}`] ||
                    item.memories?.figurine_url ||
                    item.memories?.photo_url ||
                    '/placeholder-image.png'
                  }
                  alt={item.memories?.title || 'Memory'}
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = '/placeholder-image.png';
                  }}
                />
              </div>
              <div className="item-info">
                <h4>{item.memories?.title || 'Memory'}</h4>
                <p>{item.memories?.description || ''}</p>
                <div className="item-meta">
                  <span>Quantity: {item.quantity}</span>
                  <span>Price: {formatCurrency(item.price, 'AUD')} each</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="next-steps">
          <h3>What's Next?</h3>
          <ul>
            <li>You'll receive an email confirmation shortly</li>
            <li>We'll start printing your tiniego</li>
            <li>You'll be notified when your order is ready for shipping</li>
            <li>Track your order status in "Orders"</li>
          </ul>
        </div>

        <div className="action-buttons">
          <Button className="my-studio-btn" variant="primary" onClick={() => navigate('/home')}>
            My Studio
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CheckoutSuccessPage;
